import React, { useMemo, useState } from 'react';

import { Breadcrumbs, List, ListItem } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SkeletonLoader from 'components/SkeletonLoader';
import { findGradesAndRolesOfFunction, getArrayOfIdsOfParentFunctions } from 'screens/admin/FunctionAndGrade/Helpers';
import { CheckRadio } from 'screens/home/tasksList/styles';
import styled, { css } from 'styled-components';
import {
  FlexContainer,
  HideScrollbarCSS,
  StyledBox,
  VerticallyAlignedFlexContainer,
  VerticalFlexContainer,
} from 'styles/common';

import CompanyEmployeesImage from 'assets/images/company-employees-vector-art.png';

import { StyledIcon, UncheckedCircle } from 'constants/icons';
import theme from 'constants/theme';

import { Typography5, Typography4, Typography2 } from 'uiToolkit/Typography';

import FunctionRoleSelectorList from './FunctionRoleSelectorList';

const ChildWrapper = styled.div`
  display: flex;
  width: 100%;
  // min-width: 350px;
  // max-width: 450px;
  flex-direction: row;
  align-items: center;
  padding: 1rem 1rem;
`;

const BottomDivider = styled.div`
  border-bottom: 1px solid ${theme.palette.borders.lighterGrey};
  margin: 0 ${(props) => (props.isEveryoneOption ? '0' : '1rem')};
`;

export const FunctionAndRoleListElement = ({
  label,
  checked,
  radioColor = 'green',
  hoverColor = 'textSecondary',
  isEveryoneOption = false,
}) => (
  <StyledBox width={1}>
    <ChildWrapper>
      <Typography4 variant="body2" customColor={checked ? theme.palette.text.primaryBlue : theme.palette.text.dark1}>
        {label}
      </Typography4>
      <CheckRadio
        disableRipple
        checked={checked}
        style={{ marginLeft: 'auto', padding: isEveryoneOption ? 0 : '9px 0 9px 9px' }}
        checkedIcon={<UncheckedCircle size="smallest" color={radioColor} />}
        icon={<UncheckedCircle size="smallest" color="textSecondary" />}
        hoverColor={hoverColor}
      />
    </ChildWrapper>
    <BottomDivider isEveryoneOption={isEveryoneOption} />
  </StyledBox>
);

// It's not a generic component
// Specifically designed for competencies

const ClickToSetVerticalNavBar = ({
  functions = [], // Array of type { id, header, component}
  loading = false,
  showDividers = false,
  className,
  defaultSelectedIndex = 0, // index of default selected when renders
  handleOnChange,
  currentTag,
  functionsObject,
  selectAllLevels,
  removeAllLevels,
  selectedNavProp = 'everyone',
}) => {
  const { jobFunctionRole } = currentTag;
  const [selectedNav, setSelectedNav] = useState([selectedNavProp]);
  const selectedUuids = selectedNav.map(({ uuid }) => uuid) ?? [];

  const selectedFunction = useMemo(
    () => functions.find((func) => selectedUuids.includes(func.function.uuid)),
    [selectedUuids, functions]
  );

  const getJobLevelSelected = (functionUuid, jobLevelUuid) => {
    if (jobFunctionRole) {
      const jobFunc = jobFunctionRole.find((j) => j.uuid === functionUuid);
      if (jobFunc) {
        return jobFunc?.jobRoleList?.find((j) => j.uuid === jobLevelUuid) ?? false;
      }
      return false;
    }
    return false;
  };

  const getJobLevelSelectedCount = (functionUuid, jobLevelUuid) => {
    if (jobFunctionRole) {
      const jobFunc = jobFunctionRole.find((j) => {
        if (j.parentId) {
          const arrayOfParentIds = getArrayOfIdsOfParentFunctions(functions, j)?.map(({ uuid }) => uuid);
          if (arrayOfParentIds.includes(functionUuid)) {
            return j;
          }
          if (j.uuid === functionUuid) {
            return j;
          }
        } else {
          return j.uuid === functionUuid;
        }
      });
      if (jobFunc) {
        return jobFunc?.jobRoleList?.length;
      }
      return 0;
    }
    return 0;
  };

  const getAllLevelsSelected = (functionUuid) => {
    if (jobFunctionRole && Array.isArray(jobFunctionRole)) {
      const selectedFunctionsJobLevels = jobFunctionRole.find((j) => j.uuid === functionUuid)?.jobRoleList;
      if (selectedFunctionsJobLevels?.length === Object.values(functionsObject[functionUuid].jobRoleList)?.length) {
        return true;
      }
      return false;
    }
    return false;
  };

  if (loading) {
    return <SkeletonLoader skeletons={['95%', '95%', '95%', '95%']} />;
  }

  return (
    <StyledContainer className={className} style={{ flexDirection: 'column', width: '30rem', borderRadius: '4px' }}>
      {selectedNav[0] !== 'everyone' && (
        <BreadCrumbsContainer>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            {selectedNav?.map(({ name, uuid }, index) => (
              <Typography2
                color="inherit"
                onClick={() => {
                  const newNav = selectedNav.slice(0, index + 1);
                  setSelectedNav(newNav);
                }}
              >
                {name}
              </Typography2>
            ))}
          </Breadcrumbs>
        </BreadCrumbsContainer>
      )}
      <StyledContainer className={className}>
        <StyledList>
          <StyledListItemWrapper
            isSelected={selectedNav === 'everyone'}
            handleOnClick={() => {
              setSelectedNav(['everyone']);
            }}
          >
            <Typography2
              color={selectedNav === 'everyone' ? theme.palette.text.primaryBlue : 'textSecondary'}
              cursor="pointer"
              style={{
                padding: '0.25rem',
              }}
            >
              Everyone
            </Typography2>
          </StyledListItemWrapper>
          {functions.map((func, index) => {
            const { uuid, name } = func.function ?? {};
            return (
              <StyledListItemWrapper
                key={uuid}
                isSelected={selectedUuids.includes(uuid)}
                handleOnClick={() => {
                  setSelectedNav((prev) => {
                    if (selectedUuids.includes(uuid)) {
                      return prev;
                    }
                    return [{ uuid, name }];
                  });
                }}
              >
                <Typography2
                  color={uuid === selectedNav ? theme.palette.text.primaryBlue : 'textSecondary'}
                  cursor="pointer"
                  style={{
                    padding: '0.25rem',
                    maxWidth: '7rem',
                  }}
                >
                  {name}
                </Typography2>
                {getJobLevelSelectedCount(uuid) > 0 ? (
                  <SelectedOptionsContainer>
                    <Typography5 style={{ lineHeight: 1 }} customColor={theme.palette.text.white}>
                      {getJobLevelSelectedCount(uuid)}
                    </Typography5>
                  </SelectedOptionsContainer>
                ) : null}
              </StyledListItemWrapper>
            );
          })}
        </StyledList>
        <ScrollableContainer>
          {selectedNav[0] === 'everyone' && !selectedFunction && (
            <>
              <OptionWrapper onClick={() => handleOnChange(null)}>
                <FunctionAndRoleListElement
                  label="Select everyone"
                  radioColor={theme.palette.primary.main}
                  checked={!jobFunctionRole}
                />
              </OptionWrapper>
              <VerticallyAlignedFlexContainer style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}>
                <VerticalFlexContainer style={{ alignItems: 'center', justifyContent: 'center' }}>
                  <ArrowIconContainer>
                    <StyledImage src={CompanyEmployeesImage} />
                  </ArrowIconContainer>
                  <Typography4 textalign="center" customColor={theme.palette.text.label}>
                    All employees will have this competency
                  </Typography4>
                </VerticalFlexContainer>
              </VerticallyAlignedFlexContainer>
            </>
          )}
          <FlexContainer>
            {selectedFunction &&
              selectedNav.map((nav, index) => {
                const navFunction = findGradesAndRolesOfFunction(functions, nav.uuid);
                return (
                  <FunctionRoleSelectorList
                    selectedFunction={navFunction}
                    setSelectedNav={setSelectedNav}
                    selectedUuids={selectedUuids}
                    index={index}
                    handleOnChange={handleOnChange}
                    functions={functions}
                    jobFunctionRole={jobFunctionRole}
                    selectAllLevels={selectAllLevels}
                    removeAllLevels={removeAllLevels}
                  />
                );
              })}
          </FlexContainer>
          {/* {selectedFunction?.childFunctions?.map(childFunc => {
              const { name, uuid } = childFunc.function;
              return (
                <StyledListItemWrapper
                  key={uuid}
                  isSelected={uuid === selectedNav}
                  handleOnClick={() => {
                    setSelectedNav(prev => {
                      if (!selectedUuids.includes('everyone')) {
                        if (selectedUuids.includes(uuid)) {
                          return prev;
                        }
                        return [...prev, { uuid, name }];
                      } else {
                        return [{ uuid, name }];
                      }
                    });
                  }}
                >
                  <Typography2
                    color={uuid === selectedNav ? theme.palette.text.primaryBlue : 'textSecondary'}
                    cursor="pointer"
                    style={{
                      padding: '0.25rem',
                      maxWidth: '7rem'
                    }}
                  >
                    {name}
                  </Typography2>
                </StyledListItemWrapper>
              );
            })} */}
        </ScrollableContainer>
        {/* {selectedNav.length > 1 && (
            <ClickToSetVerticalNavBar
              functions={selectedFunction.childFunctions}
              handleOnChange={handleOnChange}
              selectedNavProp={selectedNav[1]}
              currentTag={currentTag}
            />
          )} */}
      </StyledContainer>
    </StyledContainer>
  );
};

export const StyledListItemWrapper = ({ children, isSelected = false, handleOnClick }) => (
  <StyledListItem disableFocusRipple disableRipple onClick={handleOnClick} selected={isSelected} divider>
    {children}
    {isSelected ? (
      <StyledIcon color={theme.palette.primary.main}>
        <ChevronRightIcon />
      </StyledIcon>
    ) : null}
  </StyledListItem>
);

const StyledImage = styled.img`
  max-width: 100%;
`;

const StyledListItem = styled(ListItem)`
  &.Mui-selected {
    background-color: ${theme.palette.background.paper};
  }
  &.Mui-selected:hover {
    background-color: ${theme.palette.background.paper};
  }
  padding: ${(props) => (props.selected ? '1rem' : '1rem 2.5rem 1rem 1rem')};
  display: flex;
  justify-content: space-between;
`;

const ScrollableContainer = styled.div`
  position: relative;
  overflow-y: auto;
  width: 100%;
`;

const StyledList = styled(List)`
  background-color: ${theme.palette.background.paper};
  flex: 0 0 33.333333%;
  max-width: 33.333333%;
  padding: 0;
  overflow-y: auto;
  border-right: 1px solid ${theme.palette.borders.lighterGrey};
  min-width: 200px;
  ${HideScrollbarCSS};
`;

const OptionWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

const StyledContainer = styled.div`
  display: flex;
  position: relative;
  flex: 1;
  overflow: auto;
  margin: 0;
  height: 20rem;
  ${({ disabled }) =>
    disabled &&
    `
      pointer-events: none;
      opacity: 0.3;
    `};
`;

const blueBorderCSS = css`
  border-bottom: 3px solid ${(props) => props.theme.palette.primary.main};
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

const SelectedOptionsContainer = styled.div`
  height: 26px;
  width: 26px;
  background-color: ${theme.palette.background.blue.dark.v1};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
`;

const ArrowIconContainer = styled.div`
  svg {
    fill: ${(props) => props.theme.palette.borders.lightGrey};
    width: 100%;
    height: auto;
  }
  width: 120px;
`;

const BreadCrumbsContainer = styled.div`
  width: 100%;
  padding: 0.5rem;
  border-bottom: 1px solid ${theme.palette.borders.lightGrey};
`;

export default ClickToSetVerticalNavBar;
